/**
 * Hook para polling inteligente de tareas cron
 * Optimizado para pausar cuando la pestaña no está visible
 */
import { useEffect, useRef, useCallback } from 'react'

interface UseCronTaskPollingOptions {
  enabled: boolean
  interval: number
  onPoll: () => void | Promise<void>
}

/**
 * Hook que maneja polling inteligente:
 * - Pausa cuando la pestaña no está visible
 * - Poll inmediato al volver visible
 * - Cleanup automático
 */
export function useCronTaskPolling({
  enabled,
  interval,
  onPoll,
}: UseCronTaskPollingOptions): void {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isVisibleRef = useRef(true)

  const poll = useCallback(async () => {
    if (isVisibleRef.current) {
      await onPoll()
    }
  }, [onPoll])

  useEffect(() => {
    // No hacer nada si está deshabilitado
    if (!enabled) {
      return
    }

    // Manejar cambios de visibilidad de la pestaña
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden
      
      // Si vuelve a estar visible, hacer poll inmediato
      if (!document.hidden) {
        poll()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Poll inicial
    poll()

    // Configurar polling periódico
    intervalRef.current = setInterval(() => {
      poll()
    }, interval)

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [enabled, interval, poll])
}

